import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  downloadBasePriceAPI,
  fetchScenarioEditData,
  fetchScenarioLibraryData,
  fetchUserDataAPI,
  scenarioShareAPI
} from './scenarioLibraryAPI';
import { RootState } from '../../state/store';
import { setReviewTableMappings } from '../../pages/ScenarioCreation/components/ReviewTable/reviewTableSlice';
import { getCookie } from '../../utils/helper';
import axios from 'axios';

export interface ScenarioState {
  scenarios: Array<any>;
  loading: boolean;
  error: string | null;
  isShareDialogOpen: boolean;
  numberOfScenario: string[];
  selectedNumberOfScenario: string[];
  user: string[];
  selectedUser: string[];
  status: string[];
  selectedStatus: string[];
  category: string[];
  selectedCategory: string[];
  searchText: string;
  fromDate: Date | null;
  toDate: Date | null;
  isEditScenario: boolean;
  isDuplicateScenario: boolean;
  scenariosForComparison: string[];
  usersShareList: { names: string[] };
  selectedUsersShareList: string[];
  scenarioShareRes: any;
  comingFromScreen: string;
  isDownloadFileLoading : boolean
}

const initialState: ScenarioState = {
  scenarios: [],
  loading: false,
  error: null,
  isDownloadFileLoading : false,
  isShareDialogOpen: false,
  numberOfScenario: ['5', '10', '15'],
  selectedNumberOfScenario: ['5'],
  user: ['Created', 'Shared'],
  selectedUser: ['Created', 'Shared'],
  status: ['Completed', 'Draft'],
  selectedStatus: ['Completed', 'Draft'],
  category: [],
  selectedCategory: ['Core Sparkling'],
  searchText: '',
  fromDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
  toDate: new Date(),
  isEditScenario: false,
  isDuplicateScenario: false,
  scenariosForComparison: [],
  usersShareList: { names: [] },
  selectedUsersShareList: [],
  scenarioShareRes: '',
  comingFromScreen: '',
};

export const fetchScenarioLibrary = createAsyncThunk<any, any, { rejectValue: string }>(
  'scenarioLibrary/fetchScenarioLibraryData',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await fetchScenarioLibraryData(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'An unexpected error occurred');
    }
  },
);

export const fetchEditScenarioData = createAsyncThunk(
  'scenarioLibrary/fetchEditScenarioData',
  async (payload: any, { dispatch }) => {
    const response = await fetchScenarioEditData(payload);
    dispatch(setReviewTableMappings(response));
    return response;
  },
);

export const fetchUserData = createAsyncThunk<any, void, { rejectValue: string }>(
  'scenarioLibrary/fetchUserData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchUserDataAPI();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch user data');
    }
  },
);

export const scenarioShare = createAsyncThunk<any, any, { rejectValue: string }>(
  'scenarioLibrary/scenarioShare',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await scenarioShareAPI(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to share scenario');
    }
  },
);

export const downloadBasePriceFile = createAsyncThunk<any, any, { rejectValue: string }>(
  'scenarioLibrary/downloadPricingFile',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await downloadBasePriceAPI(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to share scenario');
    }
  },
);


export const uploadBasePriceFile = createAsyncThunk<any, FormData, { rejectValue: string }>(
  'scenarioLibrary/uploadPricingFile',
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const token = getCookie('accessToken');
      const backendURL = import.meta.env.VITE_APP_BASE_URL || '';
      const response = await axios.post(`${backendURL}/upload_pricing_file/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to share scenario');
    }
  },
);

const scenarioLibrarySlice = createSlice({
  name: 'scenarioLibrary',
  initialState,
  reducers: {
    openShareDialog: (state) => {
      state.isShareDialogOpen = true;
    },
    closeShareDialog: (state) => {
      state.isShareDialogOpen = false;
    },
    updateSelectedNumberOfScenarios: (state, action: PayloadAction<string[]>) => {
      state.selectedNumberOfScenario = action.payload;
    },
    updateSelectedUser: (state, action: PayloadAction<string[]>) => {
      state.selectedUser = action.payload;
    },
    updateSelectedStatus: (state, action: PayloadAction<string[]>) => {
      state.selectedStatus = action.payload;
    },
    updateSelectedCategory: (state, action: PayloadAction<string[]>) => {
      state.selectedCategory = action.payload;
    },
    updateDate: (state, action: PayloadAction<(Date | null)[]>) => {
      state.fromDate = action.payload[0];
      state.toDate = action.payload[1];
    },
    updateSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    setEditScenarioFlag: (state, action: PayloadAction<boolean>) => {
      state.isEditScenario = action.payload;
    },
    setDuplicateScenarioFlag: (state, action: PayloadAction<boolean>) => {
      state.isDuplicateScenario = action.payload;
    },
    setCategoryData: (state, action: PayloadAction<string[]>) => {
      state.category = action.payload;
    },
    setScenariosForComparison: (state, action: PayloadAction<string[]>) => {
      state.scenariosForComparison = action.payload;
    },
    updatedSelectedUserShareList: (state, action: PayloadAction<string[]>) => {
      state.selectedUsersShareList = action.payload;
    },
    setComingFromScreen: (state, action: PayloadAction<string>) => {
      state.comingFromScreen = action.payload;
    },
    resetFilters: (state) => {
      return { ...initialState, category: state.category };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchScenarioLibrary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScenarioLibrary.fulfilled, (state, action) => {
        state.loading = false;
        state.scenarios = action.payload;
        state.error = null;
      })
      .addCase(fetchScenarioLibrary.rejected, (state, action) => {
        state.loading = false;
        state.scenarios = [];
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to load data';
      })
      .addCase(fetchUserData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.loading = false;
        state.usersShareList = action.payload;
        state.error = null;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.loading = false;
        state.usersShareList = { names: [] };
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to load user data';
      })
      .addCase(scenarioShare.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(scenarioShare.fulfilled, (state, action) => {
        state.loading = false;
        state.scenarioShareRes = action.payload;
        state.error = null;
      })
      .addCase(scenarioShare.rejected, (state, action) => {
        state.loading = false;
        state.scenarioShareRes = null;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to share scenario';
      })
      .addCase(downloadBasePriceFile.pending, (state) => {
        state.isDownloadFileLoading = true;
        state.error = null;
      })
      .addCase(downloadBasePriceFile.fulfilled, (state) => {
        state.isDownloadFileLoading = false;
        state.error = null;
      })
      .addCase(downloadBasePriceFile.rejected, (state, action) => {
        state.isDownloadFileLoading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to download a file.';
      })
      .addCase(uploadBasePriceFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadBasePriceFile.rejected, (state, action) => {
        state.loading = false;
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to download a file.';
      })
      .addCase(uploadBasePriceFile.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
  },
});

export const {
  openShareDialog,
  closeShareDialog,
  updateSelectedNumberOfScenarios,
  updateSelectedStatus,
  updateSelectedUser,
  updateSelectedCategory,
  updateSearchText,
  setCategoryData,
  updateDate,
  setEditScenarioFlag,
  setDuplicateScenarioFlag,
  setScenariosForComparison,
  updatedSelectedUserShareList,
  resetFilters,
  setComingFromScreen,
} = scenarioLibrarySlice.actions;

export const selectSelectedNumberOfScenarios = (state: RootState) => state.scenarioLibrary.selectedNumberOfScenario;
export const selectSelectedUser = (state: RootState) => state.scenarioLibrary.selectedUser;
export const selectSelectedStatus = (state: RootState) => state.scenarioLibrary.selectedStatus;
export const selectUsersShareList = (state: RootState) => state.scenarioLibrary.usersShareList;
export const selectSelectedUsersShareList = (state: RootState) => state.scenarioLibrary.selectedUsersShareList;

export default scenarioLibrarySlice.reducer;
